import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Logo } from "@/components/logo";

const operatorNotes = [
  "Launch facility profiles, pricing, memberships, and staff roles.",
  "Run front desk check-in, queues, programs, and daily operations.",
  "Review booking activity, payouts, and organization readiness from one console.",
];

interface AuthShellProps {
  children: ReactNode;
  title: string;
}

export function AuthShell({ children, title }: AuthShellProps) {
  return (
    <Box
      component="main"
      sx={(theme) => ({
        minHeight: "100dvh",
        bgcolor: "background.default",
        backgroundImage: `radial-gradient(circle at 12% 16%, ${alpha(
          theme.palette.primary.main,
          0.18,
        )} 0, transparent 34%), radial-gradient(circle at 88% 86%, ${alpha(
          theme.palette.secondary.main,
          0.1,
        )} 0, transparent 30%)`,
        px: { xs: 2, sm: 3, lg: 5 },
        py: { xs: 3, sm: 4 },
      })}
    >
      <Box
        sx={{
          display: "grid",
          minHeight: { xs: "calc(100dvh - 48px)", sm: "calc(100dvh - 64px)" },
          width: "100%",
          maxWidth: 1180,
          mx: "auto",
          alignItems: "center",
          gap: { xs: 4, lg: 10 },
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1fr) 430px",
          },
        }}
      >
        <Stack
          component="aside"
          spacing={4.5}
          sx={{ display: { xs: "none", lg: "flex" }, maxWidth: 650 }}
        >
          <Link
            component={RouterLink}
            to="/"
            color="text.primary"
            underline="none"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              width: "fit-content",
            }}
          >
            <Logo size={112} style={{ flex: "0 0 auto" }} />
            <Typography sx={{ fontWeight: 700 }}>
              Badminton Warriors Business
            </Typography>
          </Link>

          <Stack spacing={2}>
            <Typography
              color="primary.main"
              sx={{ fontWeight: 700, letterSpacing: 1.4 }}
              variant="overline"
            >
              Facility operations
            </Typography>
            <Typography
              component="p"
              sx={{ fontWeight: 750, maxWidth: 620 }}
              variant="h2"
            >
              Keep every court, shift, and customer journey moving.
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ maxWidth: 590, lineHeight: 1.7 }}
            >
              The private workspace for badminton facility owners, admins, and
              operators.
            </Typography>
          </Stack>

          <Stack spacing={1.75}>
            {operatorNotes.map((note) => (
              <Stack
                direction="row"
                key={note}
                spacing={1.5}
                sx={{ alignItems: "flex-start" }}
              >
                <CheckCircleOutlineRoundedIcon
                  color="primary"
                  fontSize="small"
                  sx={{ mt: 0.25, flex: "0 0 auto" }}
                />
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ lineHeight: 1.65 }}
                >
                  {note}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        <Stack
          component="section"
          aria-labelledby="auth-page-title"
          spacing={3}
          sx={{ width: "100%", maxWidth: 430, mx: "auto" }}
        >
          <Link
            component={RouterLink}
            to="/"
            color="text.primary"
            underline="none"
            sx={{
              display: { xs: "flex", lg: "none" },
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
              width: "fit-content",
              mx: "auto",
            }}
          >
            <Logo size={112} />
            <Typography sx={{ fontWeight: 700 }}>Badminton Warriors</Typography>
          </Link>
          <Typography
            component="h1"
            id="auth-page-title"
            variant="h4"
            sx={{ fontWeight: 750 }}
          >
            {title}
          </Typography>
          {children}
        </Stack>
      </Box>
    </Box>
  );
}

/**
 * AuthProvider resolves a returned Google redirect before any Business auth
 * form may start another session exchange. Keep that lock state visually
 * consistent across owner, sign-up, and staff entry points.
 */
export function AuthStartupPending() {
  return (
    <Stack
      role="status"
      spacing={1.5}
      sx={{ alignItems: "center", py: 5 }}
    >
      <CircularProgress size={24} />
      <Typography color="text.secondary" variant="body2">
        Completing sign-in…
      </Typography>
    </Stack>
  );
}
